// packages/i18n/src/shared/utils/translatePathname.ts
import type { UrlMap } from '@tourtask/types/navigation';
import type { AppLocale } from '@tourtask/types/i18n';
import { getLocaleFromPathname } from './getLocaleFromPathname';
import { fillParams } from './fillParams';
import { matchUrlPatterns } from './matchUrlPatterns';

/**
 * Map a concrete pathname from its own locale to the equivalent path in
 * `targetLocale`, resolving parametric templates (":bookingNumber" etc.).
 * Pure and isomorphic — the client I18nContext binds it to its urlMap prop,
 * and the backoffice server layout uses it for the first-load locale
 * redirect, so both sides translate identically by construction.
 *
 * Returns the pathname unchanged when it can't be mapped (a route the map
 * doesn't cover, a legacy bookmark) — callers treat "same path back" as a
 * no-op rather than an error.
 */
export function translatePathname({
  pathname,
  targetLocale,
  urlMap,
}: {
  pathname: string;
  targetLocale: AppLocale;
  urlMap: UrlMap;
}): string {
  const sourceLocale = getLocaleFromPathname(pathname);
  const sourceMap = urlMap[sourceLocale];

  if (sourceMap) {
    // Best match first: the page's own literal path before a parametric sibling.
    for (const { code, params } of matchUrlPatterns(sourceMap, pathname)) {
      const targetPattern = urlMap[targetLocale]?.[code];
      if (targetPattern) return fillParams(targetPattern, params);
    }
  }

  return pathname;
}
